/**
 * SettingsPage — Mail-Vorlagen-Editor fuer die 3 Lead-Mail-Templates.
 *
 * Mount: laedt alle Templates via leadsApi.templates.list().
 * Layout: 3 Tabs (Erstkontakt / Follow-up 1 / Follow-up 2), pro Tab ein TemplateEditor.
 * Hinweis: Aenderungen wirken sofort fuer neue Mails — nicht retroaktiv (TPL-06).
 *
 * CI-Farben: aktiver Tab #E9FF57/#1A1A1A, inaktiv #DAD9D9.
 * NIE weisse Schrift auf Gelb.
 *
 * Phase 4 Plan 02 — TPL-02, TPL-03, TPL-04, TPL-06.
 */

import { useCallback, useEffect, useState } from 'react';
import { leadsApi } from '../services/leadsApi';
import TemplateEditor from '../components/templates/TemplateEditor';
import ListenerStatusWidget from '../components/settings/ListenerStatusWidget';
import type { LeadTemplate, LeadTemplateKey } from '../types';

type TabKey = LeadTemplateKey;

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'erstkontakt', label: 'Erstkontakt' },
  { key: 'followup_1', label: 'Follow-up 1' },
  { key: 'followup_2', label: 'Follow-up 2' },
];

export default function SettingsPage() {
  const [templates, setTemplates] = useState<LeadTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('erstkontakt');

  const loadTemplates = useCallback(async () => {
    try {
      setError(null);
      const items = await leadsApi.templates.list();
      setTemplates(items);
    } catch (err: any) {
      setError('Templates konnten nicht geladen werden. Bitte Seite neu laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const activeTemplate = templates.find((t) => t.templateKey === activeTab);

  const tabClass = (key: TabKey) =>
    activeTab === key
      ? 'px-4 py-2 text-sm font-semibold rounded-t bg-[#E9FF57] text-[#1A1A1A] border border-[#DAD9D9] border-b-0'
      : 'px-4 py-2 text-sm font-medium rounded-t bg-[#DAD9D9] text-[#1A1A1A] border border-[#DAD9D9] border-b-0 hover:bg-gray-200 transition-colors';

  return (
    <div className="max-w-3xl mx-auto">
      <ListenerStatusWidget />

      <div className="mb-6">
        <h2 className="text-xl font-bold text-[#1A1A1A]">Mail-Vorlagen</h2>
        <p className="mt-1 text-sm text-gray-600">
          Bearbeite die Texte der automatischen Teamshop-Anfragen-Mails.{' '}
          <span className="font-medium text-[#1A1A1A]">
            Aenderungen wirken sofort fuer neue Mails — nicht retroaktiv.
          </span>
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A1A1A]" />
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 border-b border-[#DAD9D9]">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={tabClass(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab-Inhalt */}
          <div className="mt-4">
            {activeTemplate ? (
              <TemplateEditor
                key={activeTemplate.templateKey}
                template={activeTemplate}
                onSaved={loadTemplates}
              />
            ) : (
              <p className="text-sm text-gray-500">
                Vorlage "{activeTab}" nicht gefunden. Bitte Seite neu laden.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
